import { FC, PropsWithChildren, useMemo } from 'react';
import { Stack, Box, Flex, BoxProps, BoxComponentProps } from '@mantine/core';
import { Text } from 'components/text/Text';
import { SortDirection } from '@tanstack/react-table';
import { IconCaretUpFilled, IconCaretDownFilled } from '@tabler/icons-react';
import styles from './Header.module.css';
import { cva, VariantProps } from 'class-variance-authority';
import { joinClassNames } from 'utils/joinClassNames';

interface HeaderRowProps extends PropsWithChildren, BoxProps {}

export const HeaderRow: FC<HeaderRowProps> = ({ children, className, ...props }) => (
    <Box className={joinClassNames(styles.row, className)} component={'tr'} {...props}>
        {children}
    </Box>
);

export type HeaderPosition = VariantProps<typeof headerPosition>['position'];
export const headerPosition = cva(styles.base, {
    variants: {
        position: {
            sticky: styles.stickyPosition,
            default: undefined,
        },
    },
    defaultVariants: {
        position: 'default',
    },
});

export const Header = ({
    children,
    sortState,
    onClick,
    isSortable = false,
    isReversed = false,
    width,
    className,
    ...props
}: PropsWithChildren<
    {
        sortState: false | SortDirection;
        onClick: ((event: unknown) => void) | undefined;
        isSortable?: boolean;
        isReversed?: boolean;
        width?: BoxComponentProps['w'];
    } & BoxComponentProps
>) => {
    const sort = useMemo(
        () => (
            <Stack gap={0}>
                <IconCaretUpFilled
                    size={14}
                    className={styles.headerSort}
                    style={{
                        ...(sortState === 'asc' && { color: 'var(--mantine-color-gray-2)' }),
                    }}
                />
                <IconCaretDownFilled
                    size={14}
                    className={styles.headerSort}
                    style={{
                        ...(sortState === 'desc' && { color: 'var(--mantine-color-gray-2)' }),
                    }}
                />
            </Stack>
        ),
        [sortState]
    );

    return (
        <Box
            component="th"
            onClick={onClick}
            className={joinClassNames(styles.header, className)}
            w={width}
            {...props}
        >
            <Flex
                p={'8px'}
                align={'center'}
                justify={'space-between'}
                direction={isReversed ? 'row-reverse' : 'row'}
                gap={'4px'}
            >
                <Text c={'gray.6'} size={'sm'} fw={500} pb={'2px'} span>
                    {children}
                </Text>
                {isSortable ? sort : <></>}
            </Flex>
        </Box>
    );
};
